package dev.wyns.ventrova.ui.theme

import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import dev.wyns.ventrova.app.AppMessage
import dev.wyns.ventrova.data.model.OpenStatus
import dev.wyns.ventrova.data.model.OpeningHours
import dev.wyns.ventrova.data.model.formatMinutes
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.day_friday
import dev.wyns.ventrova.resources.day_friday_short
import dev.wyns.ventrova.resources.day_monday
import dev.wyns.ventrova.resources.day_monday_short
import dev.wyns.ventrova.resources.day_saturday
import dev.wyns.ventrova.resources.day_saturday_short
import dev.wyns.ventrova.resources.day_sunday
import dev.wyns.ventrova.resources.day_sunday_short
import dev.wyns.ventrova.resources.day_thursday
import dev.wyns.ventrova.resources.day_thursday_short
import dev.wyns.ventrova.resources.day_tuesday
import dev.wyns.ventrova.resources.day_tuesday_short
import dev.wyns.ventrova.resources.day_wednesday
import dev.wyns.ventrova.resources.day_wednesday_short
import dev.wyns.ventrova.resources.decimal_separator
import dev.wyns.ventrova.resources.distance_kilometers
import dev.wyns.ventrova.resources.distance_meters
import dev.wyns.ventrova.resources.favorite_removed
import dev.wyns.ventrova.resources.favorite_saved
import dev.wyns.ventrova.resources.fry_oil_reported
import dev.wyns.ventrova.resources.location_off
import dev.wyns.ventrova.resources.not_available_yet
import dev.wyns.ventrova.resources.opening_hours_closed
import dev.wyns.ventrova.resources.status_always_open
import dev.wyns.ventrova.resources.status_closed
import dev.wyns.ventrova.resources.status_closes_at
import dev.wyns.ventrova.resources.status_hours_unknown
import dev.wyns.ventrova.resources.status_open
import dev.wyns.ventrova.resources.status_opens_at
import dev.wyns.ventrova.resources.status_opens_on_at
import dev.wyns.ventrova.resources.status_opens_tomorrow_at
import dev.wyns.ventrova.resources.walk_minutes
import dev.wyns.ventrova.resources.your_location
import kotlin.math.roundToInt
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.getString
import org.jetbrains.compose.resources.stringResource

/** "450 m" or "1,2 km". */
@Composable
fun distanceLabel(meters: Double): String =
    if (meters < 1000) {
        stringResource(Res.string.distance_meters, ((meters / 10).roundToInt() * 10).coerceAtLeast(10))
    } else {
        val tenths = (meters / 100).roundToInt()
        val number = "${tenths / 10}${stringResource(Res.string.decimal_separator)}${tenths % 10}"
        stringResource(Res.string.distance_kilometers, number)
    }

/** Walking time at roughly 4.6 km/h, e.g. "6 min". */
@Composable
fun walkLabel(meters: Double): String =
    stringResource(Res.string.walk_minutes, (meters / 1000 * 13).roundToInt().coerceAtLeast(1))

/** Short status ("Open"/"Closed") and when that changes ("closes at 22:00"). */
data class OpenStatusText(val status: String, val change: String?, val color: Color, val isOpen: Boolean)

@Composable
fun openStatusText(status: OpenStatus?): OpenStatusText? = when (status) {
    null -> null
    OpenStatus.AlwaysOpen -> OpenStatusText(
        status = stringResource(Res.string.status_open),
        change = stringResource(Res.string.status_always_open),
        color = VentrovaColors.Open,
        isOpen = true,
    )
    is OpenStatus.Open -> OpenStatusText(
        status = stringResource(Res.string.status_open),
        change = stringResource(Res.string.status_closes_at, formatMinutes(status.closesAt)),
        color = VentrovaColors.Open,
        isOpen = true,
    )
    is OpenStatus.Closed -> OpenStatusText(
        status = stringResource(Res.string.status_closed),
        change = when {
            status.opensAt == null || status.opensOn == null -> null
            status.opensInDays == 0 -> stringResource(Res.string.status_opens_at, formatMinutes(status.opensAt))
            status.opensInDays == 1 -> stringResource(Res.string.status_opens_tomorrow_at, formatMinutes(status.opensAt))
            else -> stringResource(Res.string.status_opens_on_at, dayName(status.opensOn, short = true), formatMinutes(status.opensAt))
        },
        color = VentrovaColors.Closed,
        isOpen = false,
    )
}

@Composable
fun hoursUnknownLabel(): String = stringResource(Res.string.status_hours_unknown)

@Composable
fun dayName(day: DayOfWeek, short: Boolean = false): String = stringResource(
    when (day) {
        DayOfWeek.MONDAY -> if (short) Res.string.day_monday_short else Res.string.day_monday
        DayOfWeek.TUESDAY -> if (short) Res.string.day_tuesday_short else Res.string.day_tuesday
        DayOfWeek.WEDNESDAY -> if (short) Res.string.day_wednesday_short else Res.string.day_wednesday
        DayOfWeek.THURSDAY -> if (short) Res.string.day_thursday_short else Res.string.day_thursday
        DayOfWeek.FRIDAY -> if (short) Res.string.day_friday_short else Res.string.day_friday
        DayOfWeek.SATURDAY -> if (short) Res.string.day_saturday_short else Res.string.day_saturday
        DayOfWeek.SUNDAY -> if (short) Res.string.day_sunday_short else Res.string.day_sunday
    },
)

/** "11:30–14:30, 18:00–22:00" or "closed". */
@Composable
fun hoursOnDay(hours: OpeningHours, day: DayOfWeek): String {
    val ranges = hours.rangesOn(day)
    if (ranges.isEmpty()) return stringResource(Res.string.opening_hours_closed)
    return ranges.joinToString(", ") { "${formatMinutes(it.start)}–${formatMinutes(it.end)}" }
}

/** Today's weekday in the device time zone. */
fun currentDayOfWeek(): DayOfWeek = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).dayOfWeek

private val AppMessage.Kind.text: StringResource
    get() = when (this) {
        AppMessage.Kind.FavoriteSaved -> Res.string.favorite_saved
        AppMessage.Kind.FavoriteRemoved -> Res.string.favorite_removed
        AppMessage.Kind.LocationOff -> Res.string.location_off
        AppMessage.Kind.YourLocation -> Res.string.your_location
        AppMessage.Kind.NotAvailableYet -> Res.string.not_available_yet
        AppMessage.Kind.FryOilCheckRequested -> Res.string.fry_oil_reported
    }

/** Text of a toast/snackbar message. */
@Composable
fun messageText(kind: AppMessage.Kind): String = stringResource(kind.text)

/** Text of a message, loaded outside composition (e.g. for a snackbar shown from an effect). */
suspend fun loadMessageText(kind: AppMessage.Kind): String = getString(kind.text)

/** Search suggestions; cuisine tags in the data are mostly English, so these match all languages. */
val SearchSuggestions = listOf("Pizza", "Burger", "Falafel", "Sushi", "Thai", "Indian", "Kebab", "Pasta")
